import React, { useState, useEffect } from "react";
import {
  Button,
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  LinearProgress,
  IconButton,
  Alert,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
} from "@mui/material";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import VideoPlayerModal from "../Shared/VideoPlayerModal";

function ContentLibrary() {
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [videoName, setVideoName] = useState("");
  const [tags, setTags] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const { id } = useUserInfo();
  const {
    putSignedUrl,
    getContentMetadata,
    addContentMetadata,
    deleteContent,
  } = useApi();

  // State for video player modal
  const [playerOpen, setPlayerOpen] = useState(false);
  const [playingVideoKey, setPlayingVideoKey] = useState(null);
  const [playingVideoName, setPlayingVideoName] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState("all");
  const [availableTags, setAvailableTags] = useState([]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contentToDelete, setContentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Function to fetch videos from content-service
  const fetchVideos = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getContentMetadata();
      const videoList = Array.isArray(data) ? data : [];
      setVideos(videoList);
      updateAvailableTags(videoList);
      if (!Array.isArray(data)) {
        console.warn("getContentMetadata did not return an array:", data);
      }
    } catch (err) {
      setError(err.message || "Failed to load videos");
      console.error("Error fetching videos:", err);
      setVideos([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch videos on component mount
  useEffect(() => {
    if (id) {
      fetchVideos();
    }
  }, [id]); // Depend on coachId

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadError(null);
    }
  };

  const generateThumbnail = async (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      // Set video to 5 seconds
      video.currentTime = 5;

      // Handle seeking
      video.onseeked = () => {
        try {
          // Set canvas size to match video dimensions
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;

          // Draw the frame at 5 seconds
          context.drawImage(video, 0, 0, canvas.width, canvas.height);

          // Convert to blob
          canvas.toBlob(
            (blob) => {
              resolve(blob);
            },
            "image/jpeg",
            0.8
          );
        } catch (error) {
          reject(error);
        }
      };

      video.onerror = () => {
        reject(new Error("Failed to load video"));
      };

      // Need to set these before setting src
      video.preload = "metadata";
      video.muted = true; // Required by some browsers
      video.playsInline = true; // Better mobile support

      // Load the video
      video.src = URL.createObjectURL(file);

      // Some browsers need us to start playing to seek
      video.play().catch(() => {
        // Ignore play errors - we just need it to seek
      });
    });
  };

  const handleUpload = async () => {
    if (!selectedFile || !videoName) {
      setUploadError("Please select a file and provide a name.");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      // Generate and upload thumbnail
      console.log("Generating thumbnail...");
      const thumbnailBlob = await generateThumbnail(selectedFile);
      console.log("Thumbnail generated successfully", {
        thumbnailSize: thumbnailBlob.size,
      });

      const thumbnailKey = `public/${id}/thumbnails/${videoName}-${Date.now()}.jpg`;
      console.log("Generated thumbnail key:", thumbnailKey);

      const { uploadUrl: thumbnailUploadUrl } = await putSignedUrl(
        thumbnailKey
      );
      console.log("Got signed URL for thumbnail:", thumbnailUploadUrl);

      const thumbnailResponse = await fetch(thumbnailUploadUrl, {
        method: "PUT",
        body: thumbnailBlob,
        headers: {
          "Content-Type": "image/jpeg",
        },
      });

      if (!thumbnailResponse.ok) {
        throw new Error(
          `Thumbnail upload failed: ${thumbnailResponse.status} ${thumbnailResponse.statusText}`
        );
      }
      console.log("Thumbnail uploaded successfully");

      // Upload video (keep video upload private)
      const videoKey = `${id}/${videoName}-${Date.now()}.mp4`;
      console.log("Generated video key:", videoKey);

      const { uploadUrl } = await putSignedUrl(videoKey);

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadUrl, true);
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round(
              (event.loaded / event.total) * 100
            );
            setUploadProgress(percentComplete);
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            console.log("Video file uploaded successfully to S3.");
            resolve(xhr.response);
          } else {
            console.error("S3 Upload Error:", xhr.statusText, xhr.response);
            reject(
              new Error(
                `S3 upload failed: ${xhr.statusText || "Unknown error"}`
              )
            );
          }
        };

        xhr.onerror = () => {
          console.error("S3 Upload Network Error:");
          reject(new Error("Network error during S3 upload"));
        };

        xhr.send(selectedFile);
      });

      console.log("Adding video metadata to content service...", {
        videoKey,
        thumbnailKey,
        name: videoName,
        tags: tags
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag),
      });

      const videoData = {
        videoKey: videoKey,
        thumbnailKey: thumbnailKey,
        name: videoName,
        tags: tags
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag),
      };
      const metadataResponse = await addContentMetadata(videoData);
      console.log("Content metadata added successfully:", metadataResponse);

      await fetchVideos();
      setSelectedFile(null);
      setVideoName("");
      setTags("");
    } catch (err) {
      setUploadError(err.message);
      console.error("Upload failed:", err);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Function to open the video player
  const handlePlayVideo = (video) => {
    if (video && video.videoKey) {
      setPlayingVideoKey(video.videoKey);
      setPlayingVideoName(video.name);
      setPlayerOpen(true);
    }
  };

  const handleClosePlayer = () => {
    setPlayerOpen(false);
    setPlayingVideoKey(null);
    setPlayingVideoName(null);
  };

  // Function to extract unique tags from videos
  const updateAvailableTags = (videoList) => {
    const tags = new Set();
    videoList.forEach((video) => {
      video.tags?.forEach((tag) => tags.add(tag));
    });
    setAvailableTags(Array.from(tags).sort());
  };

  // Filter videos based on search query and selected tag
  const filteredVideos = videos.filter((video) => {
    const matchesSearch =
      video.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      video.tags?.some((tag) =>
        tag.toLowerCase().includes(searchQuery.toLowerCase())
      );

    const matchesTag =
      selectedTag === "all" || video.tags?.includes(selectedTag);

    return matchesSearch && matchesTag;
  });

  const handleDeleteClick = (event, video) => {
    event.stopPropagation(); // Prevent video from playing when clicking delete
    setContentToDelete(video);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!contentToDelete) return;

    setIsDeleting(true);
    try {
      await deleteContent(contentToDelete.contentId);
      await fetchVideos(); // Refresh the list
      setDeleteDialogOpen(false);
      setContentToDelete(null);
    } catch (error) {
      console.error("Error deleting content:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setContentToDelete(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Content Library
      </Typography>

      <Grid container spacing={4}>
        {/* Upload Form Column */}
        <Grid item xs={12} md={4}>
          <Box
            component="form"
            sx={{
              p: 2,
              border: "1px solid grey",
              borderRadius: "4px",
              height: "100%",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Upload New Video
            </Typography>
            <TextField
              label="Video Name"
              value={videoName}
              onChange={(e) => setVideoName(e.target.value)}
              fullWidth
              margin="normal"
              required
              disabled={isUploading}
            />
            <TextField
              label="Tags (comma-separated)"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              fullWidth
              margin="normal"
              disabled={isUploading}
            />
            <Button
              variant="contained"
              component="label"
              disabled={isUploading}
              sx={{ mr: 2, mt: 2 }}
            >
              Choose Video
              <input
                type="file"
                hidden
                accept="video/*"
                onChange={handleFileChange}
              />
            </Button>
            {selectedFile && (
              <Typography component="span" sx={{ mr: 2 }}>
                {selectedFile.name}
              </Typography>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disabled={!selectedFile || !videoName || isUploading}
              sx={{ mt: 2 }}
            >
              {isUploading ? <CircularProgress size={24} /> : "Upload Video"}
            </Button>
            {isUploading && (
              <Box sx={{ width: "100%", mt: 1 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                >{`${uploadProgress}%`}</Typography>
              </Box>
            )}
            {uploadError && (
              <Typography color="error" sx={{ mt: 1 }}>
                Upload failed: {uploadError}
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Videos List Column */}
        <Grid item xs={12} md={8}>
          <Box>
            <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
              <TextField
                placeholder="Search videos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Filter by Tag</InputLabel>
                <Select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  label="Filter by Tag"
                >
                  <MenuItem value="all">All Tags</MenuItem>
                  {availableTags.map((tag) => (
                    <MenuItem key={tag} value={tag}>
                      {tag}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {isLoading && (
              <Container sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Container>
            )}
            {error && (
              <Alert severity="error" sx={{ my: 2 }}>
                Error loading videos: {error}
              </Alert>
            )}
            {!isLoading && !error && (
              <List sx={{ width: "100%" }}>
                {filteredVideos.length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary={
                        searchQuery || selectedTag !== "all"
                          ? "No videos match your search criteria"
                          : "No videos found. Upload one using the form!"
                      }
                    />
                  </ListItem>
                )}
                {filteredVideos.map((video) => (
                  <ListItem
                    key={video.contentId}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                      py: 2,
                      transition: "background-color 0.2s",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                        "& .delete-button": {
                          opacity: 1,
                        },
                      },
                    }}
                    onClick={() => handlePlayVideo(video)}
                  >
                    {/* Thumbnail Container */}
                    <Box
                      sx={{
                        width: 120,
                        height: 68,
                        backgroundColor: "grey.200",
                        borderRadius: 1,
                        overflow: "hidden",
                        flexShrink: 0,
                        position: "relative",
                        "&:hover": {
                          "& .play-overlay": {
                            opacity: 1,
                          },
                        },
                      }}
                    >
                      {video.thumbnailKey ? (
                        <img
                          src={`https://trainer-app-videos.s3.us-east-2.amazonaws.com/${video.thumbnailKey}`}
                          alt={video.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <PlayCircleOutlineIcon
                            sx={{ fontSize: 40, color: "grey.400" }}
                          />
                        </Box>
                      )}
                      {/* Play overlay */}
                      <Box
                        className="play-overlay"
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: "rgba(0, 0, 0, 0.3)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          opacity: 0,
                          transition: "opacity 0.2s",
                        }}
                      >
                        <PlayCircleOutlineIcon
                          sx={{ color: "white", fontSize: 32 }}
                        />
                      </Box>
                    </Box>

                    {/* Video Info */}
                    <Box sx={{ flex: 1 }}>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" component="div">
                            {video.name}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="div"
                          >
                            Tags: {video.tags?.join(", ") || "None"}
                          </Typography>
                        }
                      />
                    </Box>

                    {/* Delete Button */}
                    <IconButton
                      className="delete-button"
                      onClick={(e) => handleDeleteClick(e, video)}
                      sx={{
                        opacity: 0,
                        transition: "opacity 0.2s",
                        color: "error.main",
                        "&:hover": {
                          backgroundColor: "error.light",
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Delete Content</DialogTitle>
        <DialogContent>
          Are you sure you want to delete "{contentToDelete?.name}"? This action
          cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : null}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <VideoPlayerModal
        open={playerOpen}
        onClose={handleClosePlayer}
        videoKey={playingVideoKey}
        videoName={playingVideoName}
      />
    </Box>
  );
}

export default ContentLibrary;
